import { Box, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@work4all/data';
import { settings, useSetting } from '@work4all/data/lib/settings';

import { EInvoiceFormat } from '@work4all/models/lib/Enums/EInvoiceFormat.enum';

import { DefaultIconButton } from '../../default-icon-button/DefaultIconButton';
import { FixedDataPicker } from '../fixed-data-picker/FixedDataPicker';
import { IPickerProps } from '../types';

export const E_INVOICE_FORMAT_TO_SETTING_VALUE = {
  [EInvoiceFormat.ZUG_FE_RD]: 0,
  [EInvoiceFormat.X_RECHNUNG_CII]: 1,
  [EInvoiceFormat.PDF]: 2,
  [EInvoiceFormat.X_RECHNUNG_UBL]: 3,
};

export type IEInvoiceFormatPickerProps<TMultiple extends boolean> =
  IPickerProps<{ id: string; name: string }, TMultiple>;

export function EInvoiceFormatPicker<TMultiple extends boolean>(
  props: IEInvoiceFormatPickerProps<TMultiple>
) {
  const { ...rest } = props;

  const { t } = useTranslation();
  const defaultInvoiceFormatSetting = useSetting(
    settings.defaultInvoiceFormat()
  );

  const dataSet = useMemo(() => {
    const data = [
      {
        id: EInvoiceFormat.PDF,
        name: t('E_INVOICE_FORMAT.' + EInvoiceFormat.PDF),
      },
      {
        id: EInvoiceFormat.X_RECHNUNG_CII,
        name: t('E_INVOICE_FORMAT.' + EInvoiceFormat.X_RECHNUNG_CII),
      },
      {
        id: EInvoiceFormat.X_RECHNUNG_UBL,
        name: t('E_INVOICE_FORMAT.' + EInvoiceFormat.X_RECHNUNG_UBL),
      },
      {
        id: EInvoiceFormat.ZUG_FE_RD,
        name: t('E_INVOICE_FORMAT.' + EInvoiceFormat.ZUG_FE_RD),
      },
    ];

    const defaultInvoiceFormat = data.find(
      (status) =>
        E_INVOICE_FORMAT_TO_SETTING_VALUE[status.id] ===
        defaultInvoiceFormatSetting.value
    );

    const dataWithoutDefaultInvoiceFormat = data.filter(
      (status) =>
        E_INVOICE_FORMAT_TO_SETTING_VALUE[status.id] !==
        defaultInvoiceFormatSetting.value
    );

    return defaultInvoiceFormat
      ? [defaultInvoiceFormat, ...dataWithoutDefaultInvoiceFormat]
      : dataWithoutDefaultInvoiceFormat;
  }, [defaultInvoiceFormatSetting.value, t]);

  const user = useUser();
  const isAdmin = user.isMaster;

  const renderItemContent = useCallback(
    (status) => {
      const isDefault =
        E_INVOICE_FORMAT_TO_SETTING_VALUE[status.id] ===
        defaultInvoiceFormatSetting.value;

      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          className="default-icon-button-parent"
        >
          <Typography variant="body2" noWrap>
            {status.name}
          </Typography>

          <DefaultIconButton
            isDefault={isDefault}
            disabled={!isAdmin}
            onClick={(e) => {
              e.stopPropagation();
              if (isDefault) {
                // Workaround for the fact that the default invoice format is not set
                defaultInvoiceFormatSetting.set(-1);
                return;
              }

              defaultInvoiceFormatSetting.set(
                E_INVOICE_FORMAT_TO_SETTING_VALUE[status.id]
              );
            }}
          />
        </Box>
      );
    },
    [defaultInvoiceFormatSetting, isAdmin]
  );

  return (
    <FixedDataPicker
      dataSet={dataSet}
      {...rest}
      renderItemContent={renderItemContent}
    />
  );
}
